import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import BrandedErrorPage from "./components/BrandedErrorPage";

import "./index.css";

// ES2021 String.prototype.replaceAll polyfill.
// Some older Android WebViews (Capacitor APK on Android 9/10) and legacy Safari
// lack it, which crashed third-party/bundled code with
// "TypeError: e.replaceAll is not a function".
if (typeof (String.prototype as unknown as Record<string, unknown>).replaceAll !== "function") {
  // eslint-disable-next-line no-extend-native
  Object.defineProperty(String.prototype, "replaceAll", {
    writable: true,
    configurable: true,
    value: function replaceAll(this: string, search: string | RegExp, replacement: any) {
      if (search instanceof RegExp) {
        if (!search.flags.includes("g")) {
          throw new TypeError("replaceAll must be called with a global RegExp");
        }
        return this.replace(search, replacement);
      }
      const escaped = String(search).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return this.replace(new RegExp(escaped, "g"), replacement);

    },
  });
}


// Initialize Sentry — works for both web and Capacitor (webview).
// Using @sentry/react directly avoids dual-package version mismatch with @sentry/capacitor.
Sentry.init({
  dsn: "https://3ee42bf8a0c922f29bfef052f64a9459@o4511415268540416.ingest.de.sentry.io/4511493707726928",
  environment: import.meta.env.MODE,
  // Only report from production builds — preview hot-reloads produce transient
  // hook/provider errors that aren't real user-facing bugs.
  enabled: import.meta.env.PROD,
  release: `ffa@${__APP_VERSION__}`,
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  // Only propagate trace headers to our own origin. Adding sentry-trace/baggage
  // to Supabase function calls breaks their CORS preflight ("Failed to fetch").
  tracePropagationTargets: [/^https:\/\/fantasyfootballafrica\.com/],

  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],

  // Transient/browser-environment noise that is already handled gracefully in-app
  // (chunk reload recovery below, offline banner, native plugin guards).
  ignoreErrors: [
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /error loading dynamically imported module/i,
    /ChunkLoadError/i,
    /Cannot read properties of undefined \(reading 'default'\)/i,
    /undefined is not an object \(evaluating '.*\.default'\)/i,
    /replaceAll is not a function/i,
    /Failed to fetch/i,
    /Load failed/i,

    /NetworkError when attempting to fetch resource/i,
    /Clerk: Failed to load Clerk/i,
    /failed to load script/i,
    /window\.webkit/i,
    /messageHandlers/i,
    /ResizeObserver loop/i,
    /plugin is not implemented on (android|ios)/i,
  ],
  // Errors originating from browser extensions / injected third-party scripts.
  denyUrls: [
    /extensions\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
  ],
  beforeSend(event, hint) {
    const err: any = hint?.originalException;
    const msg = String(err?.message ?? err ?? event.message ?? "");

    // Drop generic parse errors with no app frames — almost always bots/extensions
    // parsing HTML as JS.
    if (/SyntaxError|Unexpected token/i.test(msg)) {
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      const hasAppFrame = frames.some((f) => f.filename?.includes("/assets/"));
      if (!hasAppFrame) return null;
    }

    // Never report while the device is offline — these are connectivity blips.
    if (typeof navigator !== "undefined" && navigator.onLine === false) return null;

    return event;
  },
});

// Auto-recover from stale-chunk errors after a new deploy. Old tabs reference JS
// chunk filenames that no longer exist post-publish; reload once to fetch the new build.
(() => {
  const FLAG = "ffa-chunk-reloaded";
  const CHUNK_RE =
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|reading 'default'|evaluating '.*\.default'/i;


  const reloadOnce = () => {
    try {
      if (sessionStorage.getItem(FLAG)) return; // already retried — let it surface
      sessionStorage.setItem(FLAG, "1");
    } catch {
      return;
    }
    window.location.reload();
  };

  const isChunkError = (msg: string) => CHUNK_RE.test(msg);

  window.addEventListener("vite:preloadError", (e: any) => {
    e?.preventDefault?.();
    reloadOnce();
  });
  window.addEventListener("error", (e) => {
    const msg = String((e as ErrorEvent)?.error?.message ?? (e as ErrorEvent)?.message ?? "");
    if (isChunkError(msg)) reloadOnce();
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isChunkError(String((e as any)?.reason?.message ?? (e as any)?.reason ?? ""))) reloadOnce();
  });
  // Successful full load → clear the flag so a future stale-chunk error can retry once.
  window.addEventListener("load", () => {
    try {
      sessionStorage.removeItem(FLAG);
    } catch {
      /* no-op */
    }
  });
})();

// Register service worker for offline caching
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // Service worker registration failed — non-critical
    });
  });
}

const SentryErrorFallback = ({ error, resetError }: { error: unknown; resetError?: () => void }) => (
  <BrandedErrorPage error={error} resetError={resetError} />
);


createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <HelmetProvider>
      <Sentry.ErrorBoundary fallback={SentryErrorFallback}>
        <App />
      </Sentry.ErrorBoundary>
    </HelmetProvider>
  </React.StrictMode>
);
